import { useEffect, useMemo, useState, type ElementType } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Sparkles,
  UserCircle2,
  Users,
  Phone,
  Send,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Plus,
  CheckCircle2,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import {
  useAiStaffHandoff,
  useAiStaffKnowledge,
  useAiStaffMessages,
  useAiStaffQuery,
  useAiStaffReindex,
  useAiStaffSessions,
} from "@/features/aiStaff/hooks/useAiStaffWorkspace";
import {
  useAiStaffActions,
  useAiStaffTickets,
  useCreateAiStaffAction,
  useCreateAiStaffTicket,
  useUpdateAiStaffSessionStatus,
  useAiStaffUsageSummary,
  useAiStaffSettings,
  useUpdateAiStaffSettings,
} from "@/features/aiStaff/hooks/useAiStaffAutomation";
import { fetchStaff, type StaffMember } from "@/lib/api/staff";
import { useToast } from "@/hooks/use-toast";
import type {
  AiAssistantAction,
  AiAssistantActionType,
  AiStaffTicket,
  AiStaffTicketPriority,
} from "@/lib/api/aiStaffAutomation";
import type { AiStaffQuotaEvaluation, AiStaffSettings } from "@/lib/api/aiStaff";

const MESSAGE_ROLE_STYLE: Record<
  string,
  {
    bubble: string;
    wrapper: string;
    icon: ElementType;
  }
> = {
  assistant: {
    bubble:
      "bg-emerald-50 border border-emerald-200/70 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-100",
    wrapper: "items-start",
    icon: Sparkles,
  },
  staff: {
    bubble:
      "bg-white border border-gray-200/70 text-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-50",
    wrapper: "items-end",
    icon: UserCircle2,
  },
  guest: {
    bubble:
      "bg-sky-50 border border-sky-200/70 text-sky-900 dark:bg-sky-500/10 dark:border-sky-400/30 dark:text-sky-100",
    wrapper: "items-start",
    icon: Users,
  },
  system: {
    bubble:
      "bg-gray-100 border border-gray-200/70 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200",
    wrapper: "items-center",
    icon: ShieldAlert,
  },
  tool: {
    bubble:
      "bg-gray-100 border border-gray-200/70 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200",
    wrapper: "items-center",
    icon: ShieldAlert,
  },
};

const ACTION_STATUS_VARIANT: Record<AiAssistantAction["status"], "default" | "outline" | "destructive"> = {
  queued: "outline",
  succeeded: "default",
  failed: "destructive",
};

const ACTION_LABELS: Record<AiAssistantActionType, string> = {
  "notification.send": "Notification",
  "ticket.open": "Open ticket",
  "ticket.update": "Update ticket",
  "ticket.resolve": "Resolve ticket",
  "assignment.update": "Assign",
  "note.append": "Note",
  "draft.create": "Create draft",
};

const formatRole = (role: string): string => {
  switch (role) {
    case "assistant":
    case "staff":
    case "guest":
    case "system":
    case "tool":
      return role;
    default:
      return "system";
  }
};

const MAX_MESSAGES_PER_SESSION = 50;

const readMetadataString = (metadata: Record<string, unknown> | undefined, key: string): string | null => {
  if (!metadata) {
    return null;
  }
  const rawValue = metadata[key];
  if (typeof rawValue !== "string") {
    return null;
  }
  const trimmed = rawValue.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const extractGuestDetails = (
  metadata: Record<string, unknown> | undefined,
): { name: string | null; phone: string | null } => ({
  name: readMetadataString(metadata, "guestName"),
  phone: readMetadataString(metadata, "guestPhone"),
});

type AiStaffCopilotWorkspaceProps = {
  settings: AiStaffSettings;
  onToggleCopilot: (enabled: boolean) => void;
  isToggling: boolean;
};

type AiStaffSetupGateProps = {
  settings: AiStaffSettings;
  isSaving: boolean;
  onToggleCopilot: (enabled: boolean) => void;
  onRetry: () => void;
};

const AiStaffCopilotWorkspace = ({
  settings,
  onToggleCopilot,
  isToggling,
}: AiStaffCopilotWorkspaceProps) => {
  const { t, i18n } = useTranslation(["common", "aiStaff"]);
  const { formatRelativeTime, formatNumber, formatPercent } = useLocaleFormatter();
  const { toast } = useToast();

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [activePanelTab, setActivePanelTab] = useState<"tickets" | "roster">("tickets");
  const [messageDraft, setMessageDraft] = useState("");
  const [previewOnly, setPreviewOnly] = useState(false);
  const [handoffNotes, setHandoffNotes] = useState("");
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketPriority, setTicketPriority] = useState<AiStaffTicketPriority>("normal");
  const [actionType, setActionType] = useState<AiAssistantActionType>("draft.create");
  const [actionDryRun, setActionDryRun] = useState(true);
  const [actionChannel, setActionChannel] = useState<"email" | "sms">("email");
  const [actionRecipient, setActionRecipient] = useState("");
  const [actionTemplateId, setActionTemplateId] = useState("order.ready");
  const [actionNotes, setActionNotes] = useState("");
  const [lastUsageSnapshot, setLastUsageSnapshot] = useState<{
    status: AiStaffQuotaEvaluation["status"];
    usageChats: number;
    quota: number | null;
    remainingChats: number | null;
    warningMessage?: string | null;
  } | null>(null);
  const [isKillSwitchOpen, setIsKillSwitchOpen] = useState(false);

  const sessionsQuery = useAiStaffSessions({ status: "open", limit: 25 });
  const knowledgeQuery = useAiStaffKnowledge({ limit: 6, visibility: "staff", locale: i18n.language });
  const messagesQuery = useAiStaffMessages(selectedSessionId, { limit: MAX_MESSAGES_PER_SESSION });
  const queryMutation = useAiStaffQuery();
  const reindexMutation = useAiStaffReindex();
  const handoffMutation = useAiStaffHandoff(selectedSessionId);
  const ticketsQuery = useAiStaffTickets({ limit: 20 });
  const actionsQuery = useAiStaffActions(
    selectedSessionId ? { sessionId: selectedSessionId, limit: 25 } : {},
  );
  const createTicketMutation = useCreateAiStaffTicket();
  const updateSessionStatusMutation = useUpdateAiStaffSessionStatus();
  const createActionMutation = useCreateAiStaffAction();
  const usageSummaryQuery = useAiStaffUsageSummary();
  const usageSummary = usageSummaryQuery.data;

  const staffRosterQuery = useQuery({
    queryKey: ["ai-staff", "roster"],
    staleTime: 60_000,
    queryFn: ({ signal }) => fetchStaff({ signal }),
  });

  const roster: StaffMember[] = useMemo(() => staffRosterQuery.data?.staff ?? [], [staffRosterQuery.data]);
  const tickets = useMemo(() => ticketsQuery.data?.tickets ?? [], [ticketsQuery.data]);
  const sessionTickets = useMemo(
    () => tickets.filter((ticket) => ticket.sessionId === selectedSessionId),
    [tickets, selectedSessionId],
  );
  const selectedSession = useMemo(
    () => sessionsQuery.data?.find((session) => session.id === selectedSessionId) ?? null,
    [sessionsQuery.data, selectedSessionId],
  );
  const selectedGuestDetails = useMemo(
    () => extractGuestDetails(selectedSession?.metadata),
    [selectedSession],
  );
  const selectedTicket = useMemo(
    () => sessionTickets.find((ticket) => ticket.id === selectedTicketId) ?? null,
    [sessionTickets, selectedTicketId],
  );
  const timelineActions = actionsQuery.data?.actions ?? [];
  const staffOptions = useMemo(
    () =>
      roster.map((member) => ({
        id: member.id,
        label: member.displayName ?? member.name ?? member.email,
      })),
    [roster],
  );

  useEffect(() => {
    if (sessionsQuery.data && !selectedSessionId && sessionsQuery.data.length > 0) {
      setSelectedSessionId(sessionsQuery.data[0]?.id ?? null);
    }
  }, [selectedSessionId, sessionsQuery.data]);

  useEffect(() => {
    if (selectedSession) {
      setSelectedTicketId(selectedSession.lastTicketId ?? null);
    } else {
      setSelectedTicketId(null);
    }
  }, [selectedSession]);

  const handleSelectSession = (sessionId: string | null) => {
    setSelectedSessionId(sessionId);
  };

  const handleCopyPhone = (phoneNumber: string) => {
    if (!phoneNumber) {
      return;
    }
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toast({
        title: t("common.status.error", { defaultValue: "Something went wrong" }),
        description: t("aiStaff.conversation.copyPhoneError", {
          defaultValue: "We couldn't copy the phone number. Try again.",
        }),
        variant: "destructive",
      });
      return;
    }
    navigator.clipboard
      .writeText(phoneNumber)
      .then(() => {
        toast({
          title: t("aiStaff.conversation.copyPhoneSuccess", {
            defaultValue: "Phone number copied.",
          }),
        });
      })
      .catch(() => {
        toast({
          title: t("common.status.error", { defaultValue: "Something went wrong" }),
          description: t("aiStaff.conversation.copyPhoneError", {
            defaultValue: "We couldn't copy the phone number. Try again.",
          }),
          variant: "destructive",
        });
      });
  };

  const handleConfirmKillSwitch = () => {
    onToggleCopilot(false);
    setIsKillSwitchOpen(false);
  };

  const handleSelectTicket = (ticket: AiStaffTicket) => {
    setSelectedTicketId(ticket.id);
    setActivePanelTab("tickets");
  };

  const handleSendMessage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = messageDraft.trim();
    if (!trimmedMessage) {
      return;
    }

    queryMutation.mutate(
      {
        conversationId: selectedSessionId ?? undefined,
        locationId: selectedSession?.locationId ?? undefined,
        message: trimmedMessage,
        previewOnly,
        locale: i18n.language,
      },
      {
        onSuccess: (data) => {
          setMessageDraft("");
          setSelectedSessionId(data.conversationId);
          setLastUsageSnapshot({
            status: data.quota.status,
            usageChats: data.quota.usageChats,
            quota: data.quota.quota,
            remainingChats: data.quota.remainingChats,
            warningMessage: data.quota.message,
          });
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleReindex = () => {
    reindexMutation.mutate(
      { locationId: selectedSession?.locationId ?? undefined },
      {
        onSuccess: () => {
          toast({
            title: t("aiStaff.knowledge.refresh", { defaultValue: "Refresh knowledge" }),
            description: t("aiStaff.knowledge.updatedLabel", {
              defaultValue: "Updated {{timeAgo}}",
              timeAgo: formatRelativeTime(new Date()),
            }),
          });
        },
        onError: () => {
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description: t("common.status.tryAgain", { defaultValue: "Please try again." }),
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleCreateTicket = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSessionId) {
      toast({
        title: t("common.status.error", { defaultValue: "Something went wrong" }),
        description: t("aiStaff.tickets.sessionRequired", {
          defaultValue: "Select a conversation before creating a ticket.",
        }),
        variant: "destructive",
      });
      return;
    }

    createTicketMutation.mutate(
      {
        sessionId: selectedSessionId,
        title: ticketTitle.trim() || undefined,
        priority: ticketPriority,
      },
      {
        onSuccess: (ticket) => {
          setTicketTitle("");
          setSelectedTicketId(ticket.id);
          toast({
            title: t("aiStaff.tickets.created", { defaultValue: "Ticket created" }),
            description: ticket.title ?? ticket.id,
          });
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleMarkTicketResolved = (ticket: AiStaffTicket) => {
    updateSessionStatusMutation.mutate(
      {
        sessionId: ticket.sessionId,
        ticketId: ticket.id,
        ticketStatus: "resolved",
        automationState: "resolved",
        resolutionNotes: t("aiStaff.tickets.resolvedNote", {
          defaultValue: "Ticket resolved by staff.",
        }),
      },
      {
        onSuccess: () => {
          toast({
            title: t("aiStaff.tickets.resolved", { defaultValue: "Ticket marked resolved" }),
          });
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleAssignTicket = (ticket: AiStaffTicket, staffId: string | null) => {
    updateSessionStatusMutation.mutate(
      {
        sessionId: ticket.sessionId,
        ticketId: ticket.id,
        assignedStaffId: staffId,
      },
      {
        onSuccess: () => {
          toast({
            title: t("aiStaff.tickets.assigned", { defaultValue: "Ticket owner updated" }),
          });
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleHandoff = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSessionId) {
      return;
    }
    handoffMutation.mutate(
      {
        handoffType: "escalation",
        notes: handoffNotes.trim() || undefined,
        targetTicketId: selectedTicketId ?? undefined,
      },
      {
        onSuccess: () => {
          setHandoffNotes("");
          toast({
            title: t("aiStaff.handoff.success", { defaultValue: "Handoff recorded." }),
          });
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleSubmitAction = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSessionId) {
      toast({
        title: t("common.status.error", { defaultValue: "Something went wrong" }),
        description: t("aiStaff.actions.sessionRequired", {
          defaultValue: "Select a conversation before triggering automation.",
        }),
        variant: "destructive",
      });
      return;
    }

    const payload =
      actionType === "notification.send"
        ? {
            templateId: actionTemplateId.trim() || 'order.ready',
            recipient: actionRecipient.trim(),
            channel: actionChannel,
            data: {
              notes: actionNotes.trim(),
            },
          }
        : {
            notes: actionNotes.trim(),
          };

    createActionMutation.mutate(
      {
        sessionId: selectedSessionId,
        ticketId: selectedTicketId ?? undefined,
        actionType,
        dryRun: actionDryRun,
        channel: actionType === "notification.send" ? actionChannel : undefined,
        payload,
      },
      {
        onSuccess: (result) => {
          toast({
            title: t("aiStaff.actions.executed", { defaultValue: "Automation queued" }),
            description:
              result.ticket?.title ??
              t("aiStaff.actions.completed", { defaultValue: "Action recorded successfully." }),
          });
          if (result.ticket) {
            setSelectedTicketId(result.ticket.id);
          }
        },
        onError: (error) => {
          const description =
            error instanceof Error
              ? error.message
              : t("common.status.tryAgain", { defaultValue: "Please try again." });
          toast({
            title: t("common.status.error", { defaultValue: "Something went wrong" }),
            description,
            variant: "destructive",
          });
        },
      },
    );
  };

  const formatTicketStatus = (status: AiStaffTicket["status"]) =>
    t(`aiStaff.tickets.status.${status}`, { defaultValue: status.replace(/_/g, " ") });

  const formatTicketPriority = (priority: AiStaffTicket["priority"]) =>
    t(`aiStaff.tickets.priority.${priority}`, { defaultValue: priority });

  const formatActionTypeLabel = (type: AiAssistantActionType) =>
    t(`aiStaff.actions.type.${type}`, { defaultValue: ACTION_LABELS[type] });

  const quotaStatusLabel = useMemo(() => {
    if (!lastUsageSnapshot) {
      return t("aiStaff.usage.unknownLabel", { defaultValue: "Usage data unavailable" });
    }
    return t(`aiStaff.usage.status.${lastUsageSnapshot.status}`, {
      defaultValue: t("aiStaff.usage.unknownLabel", { defaultValue: "Usage data unavailable" }),
    });
  }, [lastUsageSnapshot, t]);

  const quotaUsageLabel = useMemo(() => {
    if (!lastUsageSnapshot) {
      return null;
    }
    if (lastUsageSnapshot.quota === null) {
      return t("aiStaff.usage.unlimitedLabel", { defaultValue: "Unlimited usage" });
    }
    return t("aiStaff.usage.label", {
      defaultValue: "{{used}} of {{quota}} chats used",
      used: lastUsageSnapshot.usageChats,
      quota: lastUsageSnapshot.quota,
    });
  }, [lastUsageSnapshot, t]);

  const messageSending = queryMutation.isPending;
  const isTicketMutationPending = createTicketMutation.isPending || updateSessionStatusMutation.isPending;
  const isActionPending = createActionMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <Card className="border-gray-200/70 bg-white/90 backdrop-blur-md dark:border-gray-800/80 dark:bg-gray-900/80">
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                {t("aiStaff.header.title", { defaultValue: "AI Staff Copilot" })}
              </CardTitle>
              <CardDescription className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                {t("aiStaff.header.subtitle", {
                  defaultValue:
                    "Chat with the assistant, review AI drafts, and coordinate escalations alongside your team.",
                })}
              </CardDescription>
            </div>
            <div className="flex flex-col items-start gap-2 lg:items-end">
              <div className="flex items-center gap-2">
                <Switch
                  id="ai-copilot-availability-toggle"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => onToggleCopilot(checked)}
                  disabled={isToggling}
                  aria-label={t("aiStaff.settings.toggleLabel", { defaultValue: "Enable AI Staff Copilot" })}
                />
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  {settings.enabled
                    ? t("aiStaff.settings.enabledStatus", { defaultValue: "Copilot is live" })
                    : t("aiStaff.settings.disabledStatus", { defaultValue: "Copilot is off" })}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[11px] font-semibold uppercase">
                  {settings.includedChats > 0
                    ? t("aiStaff.settings.includedChats", {
                        defaultValue: "{{count}} chats/month included",
                        count: formatNumber(settings.includedChats),
                      })
                    : t("aiStaff.settings.unlimitedChats", { defaultValue: "Unlimited chats included" })}
                </Badge>
                {settings.smsPassThrough ? (
                  <Badge variant="outline" className="text-[11px] font-semibold uppercase">
                    {t("aiStaff.settings.smsPassThrough", { defaultValue: "SMS passthrough active" })}
                  </Badge>
                ) : null}
                <AlertDialog open={isKillSwitchOpen} onOpenChange={setIsKillSwitchOpen}>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      size="xs"
                      variant="destructive"
                      disabled={!settings.enabled || isToggling}
                    >
                      {t("aiStaff.settings.pauseButton", { defaultValue: "Disable Braisey" })}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("aiStaff.settings.pauseConfirmTitle", {
                          defaultValue: "Turn off AI Staff Copilot?",
                        })}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-sm">
                        {t("aiStaff.settings.pauseConfirmDescription", {
                          defaultValue:
                            "Guests and staff won't be able to start AI chats until you turn it back on.",
                        })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t("aiStaff.settings.pauseConfirmCancel", { defaultValue: "Keep enabled" })}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleConfirmKillSwitch}
                        className="bg-destructive text-white hover:bg-destructive/90 focus:ring-destructive"
                      >
                        {t("aiStaff.settings.pauseConfirmAction", { defaultValue: "Yes, disable it" })}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleSelectSession(null)}
              className="text-xs"
            >
              {t("aiStaff.conversation.startNew", { defaultValue: "Start new conversation" })}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleReindex}
              disabled={reindexMutation.isPending}
              aria-label={t("aiStaff.knowledge.refresh", { defaultValue: "Refresh knowledge" })}
            >
              {reindexMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-6">
          <Card className="flex h-[600px] flex-col border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  {t("aiStaff.conversation.title", { defaultValue: "Conversation" })}
                </CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedSession?.topic ?? t("aiStaff.conversation.title", { defaultValue: "Conversation" })}
                </CardDescription>
                {selectedSession ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
                    <span className="inline-flex items-center gap-1 font-medium text-gray-700 dark:text-gray-200">
                      <Users className="h-3.5 w-3.5" aria-hidden="true" />
                      {selectedGuestDetails.name ??
                        t("aiStaff.conversation.guestFallback", { defaultValue: "Guest" })}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                      {selectedGuestDetails.phone ??
                        t("aiStaff.conversation.phoneFallback", { defaultValue: "No phone captured" })}
                      {selectedGuestDetails.phone ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => handleCopyPhone(selectedGuestDetails.phone!)}
                        >
                          {t("aiStaff.conversation.copyPhone", { defaultValue: "Copy" })}
                        </Button>
                      ) : null}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <Switch
                    id="preview-only-toggle"
                    checked={previewOnly}
                    onCheckedChange={setPreviewOnly}
                    aria-label={t("aiStaff.conversation.previewOnlyLabel", {
                      defaultValue: "Preview without counting toward quota",
                    })}
                  />
                  {t("aiStaff.conversation.previewOnlyLabel", {
                    defaultValue: "Preview without counting toward quota",
                  })}
                </label>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="flex flex-1 flex-col gap-4 p-0">
              <div className="flex-1 overflow-hidden px-6 py-4">
                <ScrollArea className="h-full pr-4">
                  {selectedSessionId ? (
                    messagesQuery.isLoading ? (
                      <div className="flex h-full items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" aria-hidden="true" />
                      </div>
                    ) : messagesQuery.data && messagesQuery.data.length > 0 ? (
                      <div className="flex flex-col gap-4">
                        {messagesQuery.data.map((message) => {
                          const roleKey = formatRole(message.role);
                          const style = MESSAGE_ROLE_STYLE[roleKey] ?? MESSAGE_ROLE_STYLE.system;
                          const Icon = style.icon;
                          return (
                            <div key={message.id} className={cn("flex", style.wrapper)}>
                              <div className={cn("max-w-[85%] rounded-xl px-4 py-3 shadow-sm", style.bubble)}>
                                <div className="mb-2 flex items-center gap-2">
                                  <Icon className="h-4 w-4" aria-hidden="true" />
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    {t(`aiStaff.messages.role.${roleKey}`, {
                                      defaultValue: roleKey.charAt(0).toUpperCase() + roleKey.slice(1),
                                    })}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatRelativeTime(message.createdAt)}
                                  </span>
                                </div>
                                {message.content ? (
                                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                                ) : message.contentJson ? (
                                  <pre className="text-xs">{JSON.stringify(message.contentJson, null, 2)}</pre>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                        {t("aiStaff.conversation.empty", {
                          defaultValue: "Select a session or start a new conversation to review messages.",
                        })}
                      </div>
                    )
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                      {t("aiStaff.conversation.empty", {
                        defaultValue: "Select a session or start a new conversation to review messages.",
                      })}
                    </div>
                  )}
                </ScrollArea>
              </div>
              <Separator />
              <form onSubmit={handleSendMessage} className="space-y-3 px-6 py-4">
                <label htmlFor="ai-staff-message" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("aiStaff.conversation.inputLabel", { defaultValue: "Assistant message" })}
                </label>
                <Textarea
                  id="ai-staff-message"
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  placeholder={t("aiStaff.conversation.inputPlaceholder", {
                    defaultValue: "Ask about menu availability, guest questions, or staffing tasks...",
                  })}
                  className="min-h-[120px] resize-none"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {lastUsageSnapshot?.warningMessage ? <span>{lastUsageSnapshot.warningMessage}</span> : null}
                  </div>
                  <Button type="submit" disabled={messageSending} className="inline-flex items-center gap-2">
                    {messageSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        {t("aiStaff.conversation.sending", { defaultValue: "Sending..." })}
                      </>
                    ) : (
                      <>
                        {t("aiStaff.conversation.send", { defaultValue: "Send message" })}
                        <Send className="h-4 w-4" aria-hidden="true" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.actions.composerTitle", { defaultValue: "Assistant actions" })}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                {selectedTicket
                  ? t("aiStaff.actions.ticketTarget", {
                      defaultValue: "Linked ticket: {{title}}",
                      title: selectedTicket.title ?? selectedTicket.id,
                    })
                  : t("aiStaff.actions.noTicket", {
                      defaultValue: "Select or create a ticket to link automation.",
                    })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitAction} className="space-y-4">
                <Tabs
                  value={actionType}
                  onValueChange={(value) => setActionType(value as AiAssistantActionType)}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="draft.create">
                      {t("aiStaff.actions.tabs.draft", { defaultValue: "Draft reply" })}
                    </TabsTrigger>
                    <TabsTrigger value="notification.send">
                      {t("aiStaff.actions.tabs.notify", { defaultValue: "Notify guest" })}
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="draft.create" className="space-y-3 pt-4">
                    <Label htmlFor="action-notes">
                      {t("aiStaff.actions.draftLabel", { defaultValue: "Notes for draft" })}
                    </Label>
                    <Textarea
                      id="action-notes"
                      value={actionNotes}
                      onChange={(event) => setActionNotes(event.target.value)}
                      placeholder={t("aiStaff.actions.draftPlaceholder", {
                        defaultValue: "Summarize the assistant response or add instructions...",
                      })}
                    />
                  </TabsContent>
                  <TabsContent value="notification.send" className="space-y-3 pt-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="action-template">
                          {t("aiStaff.actions.template", { defaultValue: "Template" })}
                        </Label>
                        <Input
                          id="action-template"
                          value={actionTemplateId}
                          onChange={(event) => setActionTemplateId(event.target.value)}
                          placeholder="order.ready"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>
                          {t("aiStaff.actions.channel", { defaultValue: "Channel" })}
                        </Label>
                        <Select value={actionChannel} onValueChange={(value) => setActionChannel(value as 'email' | 'sms')}>
                          <SelectTrigger>
                            <SelectValue placeholder="email" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="action-recipient">
                        {t("aiStaff.actions.recipient", { defaultValue: "Recipient" })}
                      </Label>
                      <Input
                        id="action-recipient"
                        type="email"
                        value={actionRecipient}
                        onChange={(event) => setActionRecipient(event.target.value)}
                        placeholder="guest@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="action-message">
                        {t("aiStaff.actions.message", { defaultValue: "Message" })}
                      </Label>
                      <Textarea
                        id="action-message"
                        value={actionNotes}
                        onChange={(event) => setActionNotes(event.target.value)}
                        placeholder={t("aiStaff.actions.messagePlaceholder", {
                          defaultValue: "Share context or the body of the notification...",
                        })}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
                <div className="flex flex-col gap-3 border-t border-gray-200 pt-3 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                    <Switch checked={actionDryRun} onCheckedChange={setActionDryRun} />
                    {t("aiStaff.actions.dryRun", { defaultValue: "Preview without sending" })}
                  </label>
                  <Button
                    type="submit"
                    disabled={!selectedSessionId || isActionPending}
                    className="inline-flex items-center gap-2"
                  >
                    {isActionPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    )}
                    {t("aiStaff.actions.submit", { defaultValue: "Run action" })}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.actions.timelineTitle", { defaultValue: "Automation timeline" })}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                {t("aiStaff.actions.timelineDescription", {
                  defaultValue: "Track drafts, notifications, and ticket updates triggered by the copilot.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedSessionId ? (
                actionsQuery.isLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" />
                  </div>
                ) : timelineActions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("aiStaff.actions.timelineEmpty", { defaultValue: "No automation events yet." })}
                  </p>
                ) : (
                  <ScrollArea className="max-h-80 pr-4">
                    <div className="space-y-3">
                      {timelineActions.map((action) => (
                        <div
                          key={action.id}
                          className="rounded-lg border border-gray-200/70 p-3 shadow-sm dark:border-gray-700"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary">{formatActionTypeLabel(action.actionType)}</Badge>
                              <Badge variant={ACTION_STATUS_VARIANT[action.status]}>
                                {t(`aiStaff.actions.status.${action.status}`, {
                                  defaultValue: action.status,
                                })}
                              </Badge>
                              {action.ticketId ? (
                                <Badge variant="outline" className="text-xs">
                                  {t("aiStaff.actions.ticketBadge", {
                                    defaultValue: "Ticket",
                                  })}{" "}
                                  {action.ticketId.slice(0, 8)}
                                </Badge>
                              ) : null}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatRelativeTime(action.executedAt ?? action.createdAt)}
                            </span>
                          </div>
                          {Object.keys(action.result ?? {}).length > 0 ? (
                            <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-gray-950/5 p-2 text-[11px] text-gray-600 dark:bg-gray-50/10 dark:text-gray-300">
                              {JSON.stringify(action.result, null, 2)}
                            </pre>
                          ) : null}
                          {action.errorMessage ? (
                            <p className="mt-2 text-xs text-rose-500 dark:text-rose-400">
                              {action.errorMessage}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("aiStaff.actions.timelineNoSession", {
                    defaultValue: "Select a conversation to view automation history.",
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.sessions.title", { defaultValue: "Active sessions" })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sessionsQuery.isLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" />
                </div>
              ) : !sessionsQuery.data || sessionsQuery.data.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  {t("aiStaff.sessions.empty", { defaultValue: "No active conversations yet." })}
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {sessionsQuery.data.map((session) => {
                    const isActive = session.id === selectedSessionId;
                    const statusLabel = t(`aiStaff.sessions.status.${session.status}`, {
                      defaultValue: session.status,
                    });
                    const sessionGuest = extractGuestDetails(session.metadata);
                    const sessionGuestName =
                      sessionGuest.name ??
                      t("aiStaff.conversation.guestFallback", { defaultValue: "Guest" });
                    const sessionGuestPhone =
                      sessionGuest.phone ??
                      t("aiStaff.conversation.phoneFallback", { defaultValue: "No phone captured" });
                    const topicLabel =
                      session.topic ?? t("aiStaff.conversation.title", { defaultValue: "Conversation" });
                    return (
                      <li key={session.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectSession(session.id)}
                          className={cn(
                            "w-full rounded-lg border px-3 py-2 text-left transition-all",
                            isActive
                              ? "border-emerald-200 bg-emerald-50 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-500/10"
                              : "border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/70 dark:hover:bg-gray-800",
                          )}
                          aria-pressed={isActive}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {sessionGuestName}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{topicLabel}</span>
                            </div>
                            <Badge variant={isActive ? "default" : "outline"} className="text-xs">
                              {statusLabel}
                            </Badge>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1">
                              <Phone className="h-3 w-3" aria-hidden="true" />
                              {sessionGuestPhone}
                            </span>
                            <span>
                              {t("aiStaff.sessions.updatedLabel", {
                                defaultValue: "Updated {{timeAgo}}",
                                timeAgo: formatRelativeTime(session.updatedAt),
                              })}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.usage.title", { defaultValue: "Usage and quota" })}
              </CardTitle>
            </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{quotaStatusLabel}</Badge>
            </div>
            {quotaUsageLabel ? <p className="text-gray-600 dark:text-gray-300">{quotaUsageLabel}</p> : null}
            {lastUsageSnapshot?.warningMessage ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">{lastUsageSnapshot.warningMessage}</p>
            ) : null}
            {usageSummary ? (
              <>
                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.chats", { defaultValue: "Chats this month" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatNumber(usageSummary.chats)}
                    </p>
                    {usageSummary.includedChats !== null ? (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t("aiStaff.usage.metrics.remaining", {
                          defaultValue: "Remaining: {{count}}",
                          count: formatNumber(usageSummary.remainingChats ?? 0),
                        })}
                      </p>
                    ) : null}
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.notifications", { defaultValue: "Notifications sent" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatNumber(usageSummary.notificationsSent)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t("aiStaff.usage.metrics.sms", {
                        defaultValue: "SMS: {{count}}",
                        count: formatNumber(usageSummary.smsSent),
                      })}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.tickets", { defaultValue: "Tickets opened" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatNumber(usageSummary.ticketsOpened)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.actions", { defaultValue: "Automation actions" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatNumber(usageSummary.actionsTotal)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.tokensTotal", { defaultValue: "Tokens (in + out)" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatNumber(usageSummary.tokensTotal)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.estimatedCost", { defaultValue: "Est. token cost" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(
                        (usageSummary.estimatedTokenCostCents ?? 0) / 100,
                      )}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.cacheHitRate", { defaultValue: "Cache hit rate" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatPercent(usageSummary.cacheHitRate)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t("aiStaff.usage.metrics.cacheHitsDetail", {
                        defaultValue: "{{count}} cached replies",
                        count: formatNumber(usageSummary.cacheHits),
                      })}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">
                      {t("aiStaff.usage.metrics.handoffRate", { defaultValue: "Auto handoff rate" })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-50">
                      {formatPercent(usageSummary.handoffRate)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t("aiStaff.usage.metrics.handoffsDetail", {
                        defaultValue: "{{count}} escalations",
                        count: formatNumber(usageSummary.handoffs),
                      })}
                    </p>
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-gray-200/70 p-3 text-xs dark:border-gray-700">
                  <p className="font-semibold text-gray-700 dark:text-gray-200">
                    {t("aiStaff.usage.metrics.functionCalls", { defaultValue: "Function calls" })}
                  </p>
                  {Object.keys(usageSummary.functionCalls ?? {}).length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {Object.entries(usageSummary.functionCalls)
                        .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
                        .map(([name, count]) => (
                          <div key={name} className="flex items-center justify-between text-gray-600 dark:text-gray-300">
                            <span className="capitalize">{name.replace(/_/g, " ")}</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-50">
                              {formatNumber(count)}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-gray-500 dark:text-gray-400">
                      {t("aiStaff.usage.metrics.functionCallsEmpty", {
                        defaultValue: "No structured calls yet",
                      })}
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader className="pb-0">
              <Tabs value={activePanelTab} onValueChange={(value) => setActivePanelTab(value as 'tickets' | 'roster')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="tickets">
                    {t("aiStaff.tickets.title", { defaultValue: "Tickets" })}
                  </TabsTrigger>
                  <TabsTrigger value="roster">
                    {t("aiStaff.roster.title", { defaultValue: "Staff roster" })}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="tickets" className="pt-4">
                  <form onSubmit={handleCreateTicket} className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="ticket-title">
                        {t("aiStaff.tickets.form.title", { defaultValue: "Ticket title" })}
                      </Label>
                      <Input
                        id="ticket-title"
                        value={ticketTitle}
                        onChange={(event) => setTicketTitle(event.target.value)}
                        placeholder={t("aiStaff.tickets.form.placeholder", {
                          defaultValue: "Guest reported {{issue}}",
                          issue: "missing order",
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t("aiStaff.tickets.form.priority", { defaultValue: "Priority" })}
                      </Label>
                      <Select value={ticketPriority} onValueChange={(value) => setTicketPriority(value as AiStaffTicketPriority)}>
                        <SelectTrigger>
                          <SelectValue placeholder="normal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t("aiStaff.tickets.priority.low", { defaultValue: "Low" })}</SelectItem>
                          <SelectItem value="normal">{t("aiStaff.tickets.priority.normal", { defaultValue: "Normal" })}</SelectItem>
                          <SelectItem value="high">{t("aiStaff.tickets.priority.high", { defaultValue: "High" })}</SelectItem>
                          <SelectItem value="urgent">{t("aiStaff.tickets.priority.urgent", { defaultValue: "Urgent" })}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="submit"
                      variant="outline"
                      size="sm"
                      className="inline-flex items-center gap-2"
                      disabled={!selectedSessionId || createTicketMutation.isPending}
                    >
                      {createTicketMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      )}
                      {t("aiStaff.tickets.form.submit", { defaultValue: "Create ticket" })}
                    </Button>
                  </form>
                  <Separator className="my-3" />
                  {ticketsQuery.isLoading ? (
                    <div className="flex h-24 items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" />
                    </div>
                  ) : sessionTickets.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("aiStaff.tickets.empty", { defaultValue: "No tickets linked to this conversation yet." })}
                    </p>
                  ) : (
                    <ScrollArea className="max-h-64 pr-2">
                      <div className="space-y-3">
                        {sessionTickets.map((ticket) => {
                          const isSelected = ticket.id === selectedTicketId;
                          return (
                            <div key={ticket.id} className="space-y-2">
                              <button
                                type="button"
                                onClick={() => handleSelectTicket(ticket)}
                                className={cn(
                                  "w-full rounded-lg border px-3 py-2 text-left text-sm transition",
                                  isSelected
                                    ? "border-emerald-200 bg-emerald-50 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-500/10"
                                    : "border-gray-200/70 bg-gray-50 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-800/70 dark:hover:bg-gray-800",
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                                    {ticket.title ?? t("aiStaff.tickets.untitled", { defaultValue: "Untitled ticket" })}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {formatTicketStatus(ticket.status)}
                                  </Badge>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                  <span>{formatTicketPriority(ticket.priority)}</span>
                                  <span>{formatRelativeTime(ticket.updatedAt ?? ticket.createdAt)}</span>
                                </div>
                              </button>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="outline"
                                  onClick={() => handleSelectTicket(ticket)}
                                >
                                  {t("aiStaff.tickets.view", { defaultValue: "Focus" })}
                                </Button>
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="ghost"
                                  disabled={isTicketMutationPending}
                                  onClick={() => handleMarkTicketResolved(ticket)}
                                >
                                  {t("aiStaff.tickets.markResolved", { defaultValue: "Mark resolved" })}
                                </Button>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                  {t("aiStaff.tickets.assignLabel", { defaultValue: "Assign to" })}
                                </Label>
                                <Select
                                  value={ticket.assignedStaffId ?? "unassigned"}
                                  onValueChange={(value) =>
                                    handleAssignTicket(
                                      ticket,
                                      value === "unassigned" ? null : value,
                                    )
                                  }
                                  disabled={isTicketMutationPending}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue
                                      placeholder={t("aiStaff.tickets.assignPlaceholder", {
                                        defaultValue: "Select team member",
                                      })}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">
                                      {t("aiStaff.tickets.assignNone", { defaultValue: "Unassigned" })}
                                    </SelectItem>
                                    {staffOptions.map((option) => (
                                      <SelectItem key={option.id} value={option.id}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>
                <TabsContent value="roster" className="pt-4">
                  {staffRosterQuery.isLoading ? (
                    <div className="flex h-24 items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" />
                    </div>
                  ) : roster.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("aiStaff.roster.empty", { defaultValue: "No staff members visible." })}
                    </p>
                  ) : (
                    <ScrollArea className="max-h-64 pr-2">
                      <ul className="space-y-3 text-sm">
                        {roster.map((member) => (
                          <li
                            key={member.id}
                            className="flex items-center justify-between rounded-lg border border-gray-200/70 px-3 py-2 dark:border-gray-700"
                          >
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {member.displayName ?? member.name ?? member.email}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{member.role}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {member.currentStatus ?? member.status ?? "active"}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </ScrollArea>
                  )}
                </TabsContent>
              </Tabs>
            </CardHeader>
          </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.handoff.title", { defaultValue: "Escalate or assign" })}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                {t("aiStaff.handoff.description", {
                  defaultValue: "Send this session to a teammate when the AI needs help or a human decision.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleHandoff} className="space-y-3">
                <label htmlFor="ai-handoff-notes" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("aiStaff.handoff.notesLabel", { defaultValue: "Notes for teammate" })}
                </label>
                <Textarea
                  id="ai-handoff-notes"
                  value={handoffNotes}
                  onChange={(event) => setHandoffNotes(event.target.value)}
                  placeholder={t("aiStaff.handoff.notesPlaceholder", { defaultValue: "Provide context for the handoff" })}
                  className="min-h-[100px]"
                  disabled={!selectedSessionId || handoffMutation.isPending}
                />
                <Button
                  type="submit"
                  disabled={!selectedSessionId || handoffMutation.isPending}
                  className="w-full"
                >
                  {handoffMutation.isPending
                    ? t("aiStaff.handoff.submitting", { defaultValue: "Recording..." })
                    : t("aiStaff.handoff.submit", { defaultValue: "Record handoff" })}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
                {t("aiStaff.knowledge.title", { defaultValue: "Knowledge base" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {knowledgeQuery.isLoading ? (
                <div className="flex h-24 items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" />
                </div>
              ) : knowledgeQuery.data && knowledgeQuery.data.length > 0 ? (
                <ul className="space-y-3">
                  {knowledgeQuery.data.map((chunk) => (
                    <li key={chunk.id} className="rounded-lg border border-gray-200/70 p-3 dark:border-gray-700">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {chunk.title ?? chunk.sourceType}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {t(`aiStaff.knowledge.visibility.${chunk.visibility}`, {
                            defaultValue: chunk.visibility,
                          })}
                        </Badge>
                      </div>
                      <p className="line-clamp-3 text-xs text-gray-600 dark:text-gray-300">{chunk.content}</p>
                      <p className="mt-2 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t("aiStaff.knowledge.updatedLabel", {
                          defaultValue: "Updated {{timeAgo}}",
                          timeAgo: formatRelativeTime(chunk.updatedAt),
                        })}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("aiStaff.knowledge.empty", { defaultValue: "No knowledge chunks available." })}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const AiStaffSettingsSkeleton = () => (
  <div className="flex flex-col gap-6">
    <Card className="border-gray-200/70 bg-white/90 backdrop-blur-md dark:border-gray-800/80 dark:bg-gray-900/80">
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-3 h-4 w-64" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-9 w-40" />
      </CardContent>
    </Card>
  </div>
);

const AiStaffSettingsError = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation(["aiStaff"]);
  return (
    <Alert variant="destructive" className="max-w-3xl">
      <AlertTitle>
        {t("aiStaff.settings.loadErrorTitle", { defaultValue: "Unable to load AI settings" })}
      </AlertTitle>
      <AlertDescription className="text-sm">
        {t("aiStaff.settings.loadErrorDescription", {
          defaultValue: "Refresh the page or try again shortly.",
        })}
      </AlertDescription>
      <div className="mt-3">
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {t("aiStaff.settings.retry", { defaultValue: "Try again" })}
        </Button>
      </div>
    </Alert>
  );
};

const AiStaffSetupGate = ({
  settings,
  isSaving,
  onToggleCopilot,
  onRetry,
}: AiStaffSetupGateProps) => {
  const { t } = useTranslation(["aiStaff"]);
  const { formatNumber } = useLocaleFormatter();
  const toggleDisabled = isSaving || (!settings.providerConfigured && !settings.enabled);

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <Card className="border-gray-200/70 bg-white/95 backdrop-blur dark:border-gray-800/80 dark:bg-gray-900/80">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-50">
            {t("aiStaff.settings.title", { defaultValue: "Copilot availability" })}
          </CardTitle>
          <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
            {t("aiStaff.settings.description", {
              defaultValue:
                "Turn the assistant on or off for your team. Changes apply immediately to staff and guest experiences.",
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="ai-copilot-setup-toggle"
              checked={settings.enabled}
              onCheckedChange={(checked) => onToggleCopilot(checked)}
              disabled={toggleDisabled}
              aria-label={t("aiStaff.settings.toggleLabel", { defaultValue: "Enable AI Staff Copilot" })}
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {settings.enabled
                ? t("aiStaff.settings.enabledStatusDetailed", {
                    defaultValue: "Copilot is live for this tenant.",
                  })
                : t("aiStaff.settings.disabledStatusDetailed", {
                    defaultValue: "Copilot is currently off.",
                  })}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[11px] font-semibold uppercase">
              {settings.includedChats > 0
                ? t("aiStaff.settings.includedChats", {
                    defaultValue: "{{count}} chats/month included",
                    count: formatNumber(settings.includedChats),
                  })
                : t("aiStaff.settings.unlimitedChats", { defaultValue: "Unlimited chats included" })}
            </Badge>
            {settings.smsPassThrough ? (
              <Badge variant="outline" className="text-[11px] font-semibold uppercase">
                {t("aiStaff.settings.smsPassThrough", { defaultValue: "SMS passthrough active" })}
              </Badge>
            ) : null}
          </div>
          {!settings.providerConfigured ? (
            <Alert variant="destructive" className="border border-destructive/40 bg-destructive/10">
              <AlertTitle>
                {t("aiStaff.settings.providerMissingTitle", {
                  defaultValue: "Requires OpenAI API key",
                })}
              </AlertTitle>
              <AlertDescription className="text-sm">
                {t("aiStaff.settings.providerMissing", {
                  defaultValue:
                    "Add an OpenAI API key to your environment to finish enabling the copilot.",
                })}
              </AlertDescription>
              <div className="mt-3">
                <Button type="button" size="sm" variant="outline" onClick={onRetry}>
                  {t("aiStaff.settings.refresh", { defaultValue: "Refresh status" })}
                </Button>
              </div>
            </Alert>
          ) : (
            <div>
              <Button type="button" size="sm" variant="outline" onClick={onRetry} disabled={isSaving}>
                {t("aiStaff.settings.refresh", { defaultValue: "Refresh status" })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AiStaffCopilotPage = () => {
  const { t } = useTranslation(["aiStaff"]);
  const { toast } = useToast();
  const settingsQuery = useAiStaffSettings();
  const updateSettingsMutation = useUpdateAiStaffSettings();

  const handleToggleCopilot = (nextEnabled: boolean) => {
    updateSettingsMutation.mutate(
      { enabled: nextEnabled },
      {
        onSuccess: () => {
          toast({
            title: t(nextEnabled ? "aiStaff.settings.enableSuccess" : "aiStaff.settings.disableSuccess", {
              defaultValue: nextEnabled ? "AI Staff Copilot enabled." : "AI Staff Copilot disabled.",
            }),
            description: t("aiStaff.settings.saved", {
              defaultValue: "Your changes take effect immediately.",
            }),
          });
        },
        onError: (error) => {
          const message =
            error instanceof Error
              ? error.message
              : t("aiStaff.settings.error", {
                  defaultValue: "We couldn't update the assistant settings. Try again.",
                });
          toast({
            title: t("aiStaff.settings.errorTitle", { defaultValue: "Update failed" }),
            description: message,
            variant: "destructive",
          });
        },
      },
    );
  };

  if (settingsQuery.isLoading) {
    return <AiStaffSettingsSkeleton />;
  }

  if (settingsQuery.isError || !settingsQuery.data) {
    return <AiStaffSettingsError onRetry={() => settingsQuery.refetch()} />;
  }

  const settings = settingsQuery.data;
  const shouldShowSetup = !settings.enabled || !settings.providerConfigured;

  if (shouldShowSetup) {
    return (
      <AiStaffSetupGate
        settings={settings}
        isSaving={updateSettingsMutation.isPending}
        onToggleCopilot={handleToggleCopilot}
        onRetry={() => settingsQuery.refetch()}
      />
    );
  }

  return (
    <AiStaffCopilotWorkspace
      settings={settings}
      onToggleCopilot={handleToggleCopilot}
      isToggling={updateSettingsMutation.isPending}
    />
  );
};

export default AiStaffCopilotPage;
